import * as React from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import { useAutoDisposableTooltipState } from 'hooks';
import { timeZoneMappingService, getReadableTimeZoneDateTime } from 'services';

import type { TimeZoneSelectOption } from 'types';
import Button from '@mui/material/Button';
import { copyTextToClipboard, getParentAppLocationObject } from 'iframe';
import { CopyIcon } from 'icons';
import { Tooltip } from '@mui/material';

interface TimeZoneSummaryHeadingProps {
  selectedTz: null | TimeZoneSelectOption;
  hasResults: boolean;
}

const buildSelectedTimeZoneDateInfo = (
  selectedTz: TimeZoneSelectOption | null
) => {
  if (!selectedTz?.id) return '';

  const tzData = timeZoneMappingService.mapCorrectTimeZoneDataToDeprecated(
    selectedTz.id
  );

  return getReadableTimeZoneDateTime(tzData);
};

export const TimeZoneSummaryHeading = ({
  selectedTz,
  hasResults,
}: TimeZoneSummaryHeadingProps) => {
  const [tzSummary, setTzSummary] = React.useState(() =>
    buildSelectedTimeZoneDateInfo(selectedTz)
  );
  const { triggerTooltip, tooltipText } = useAutoDisposableTooltipState(
    'Share this view with others'
  );

  // is not that accurate, but it's good enough for now
  React.useEffect(() => {
    setTzSummary(buildSelectedTimeZoneDateInfo(selectedTz));
    const interval = setInterval(() => {
      setTzSummary(buildSelectedTimeZoneDateInfo(selectedTz));
    }, 5000);

    return () => clearInterval(interval);
  }, [selectedTz]);

  return (
    <Typography
      variant="h5"
      sx={{
        mt: '50px',
        textTransform: 'uppercase',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '10px',
      }}
    >
      <Box sx={{ textTransform: 'none' }}>
        {selectedTz ? (
          <>
            {selectedTz.label}{' '}
            <Box color="text.placeholder" display="inline-flex">
              {tzSummary}
            </Box>
          </>
        ) : (
          'Select your preferred time zone'
        )}
      </Box>

      <Tooltip title={tooltipText} placement="top">
        <Button
          size="small"
          variant="outlined"
          disabled={!hasResults}
          onClick={async () => {
            const location = await getParentAppLocationObject();
            copyTextToClipboard(location.href);
            triggerTooltip('Copied!');
          }}
        >
          <CopyIcon width={16} height={16} style={{ marginRight: '6px' }} />
          Copy results URL
        </Button>
      </Tooltip>
    </Typography>
  );
};
